import { useAuthContext } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, ErrorMessage, Input } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePasswordValidator, useSubmitValidation } from '../../..';
import { AuthPageTitle, PasswordEyeIcon } from '../../atoms';
import { AuthForm, AuthFormHeader } from '../../molecules';
import { AuthPage, AuthPageContent, RegisterSideContent } from '../../organism';

interface PasswordResetState {
    password?: string | null;
}

export const ResetPassword = () => {
    const { t } = useTranslation();
    const [passwordType, setPasswordType] = useState('password');
    const navigate = useNavigate();
    const { resetCode } = useParams({ strict: false });
    const { updateAuthState } = useAuthContext();

    const {
        register,
        getValues,
        trigger,
        watch,
        handleSubmit,
        formState: { errors }
    } = useForm<PasswordResetState>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {}
    });
    const validate = useSubmitValidation({ watch, trigger });
    const onSubmit: SubmitHandler<PasswordResetState> = (data) => {};

    const { passwordValidation, validatePassword, passwordMessages } = usePasswordValidator();

    const [passwordScore, setPasswordScore] = useState<number>(-1);
    const password = watch('password');
    useEffect(() => {
        if (password) {
            validatePassword(password).then((result) => setPasswordScore(result.score));
        } else {
            setPasswordScore(-1);
        }
    }, [password]);

    const resetPasswordMutation = useMutation(OptionsApi.login.resetPassword());
    const resetPassword = () => {
        validate(['password']).then((isValid) => {
            if (!isValid) return;

            const { password } = getValues();
            resetPasswordMutation.mutate(
                { linkId: resetCode, password },
                {
                    onSuccess: (response) => {
                        updateAuthState(response);
                        navigate({ to: '/' });
                    }
                }
            );
        });
    };

    const togglePasswordVisibility = () => {
        if (passwordType === 'password') {
            setPasswordType('text');
        } else {
            setPasswordType('password');
        }
    };

    return (
        <AuthPage>
            <AuthPageContent>
                <AuthFormHeader>
                    <AuthPageTitle testId="forgotPassword" className="mb-4 sm:mb-12">
                        {t('ResetPassword.PageHeading')}
                    </AuthPageTitle>
                    {resetPasswordMutation?.error && <ErrorMessage classNames="mb-8 w-full" apiError={resetPasswordMutation.error} />}
                </AuthFormHeader>
                <AuthForm classNames="form-auth" onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        type={passwordType}
                        label={t('Account.Label.Password')}
                        {...register('password', passwordValidation)}
                        {...passwordMessages(passwordScore, errors.password?.message)}
                    >
                        <PasswordEyeIcon passwordType={passwordType} onClick={togglePasswordVisibility} />
                    </Input>
                    <Button type="submit" isLoading={resetPasswordMutation.isPending} onClick={resetPassword} variant="primary" classNames="flex-1">
                        {t('Account.Button.ResetPassword')}
                    </Button>
                </AuthForm>
            </AuthPageContent>

            <RegisterSideContent />
        </AuthPage>
    );
};
